import {
    Alert,
    Avatar,
    Box,
    Button,
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    Link,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import { ReactComponent as LogoSvg } from "../../assets/images/svg/logo/hbscanLogo.svg";
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import bgLogin from "../../assets/images/bgLogin.png";
import { routes } from "../../routes";
import useLogin from "../../hooks/useLogin";
import Progress from "../../components/progress/Progress";
import { useTranslation } from "react-i18next";
import { ChangeEvent, useState } from "react";
import LoginTemplate from "../../components/forgotPassword/LoginTemplate";
import useSsoLogin from "../../hooks/useSsoLogin";
import MicrosoftIcon from '@mui/icons-material/Microsoft';

const AUTO_HIDE_DURATION = 6000;

interface CustomTextFieldProps {
    label: string;
    type: string;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const CustomTextField = ({ label, type, value, onChange }: CustomTextFieldProps) => {
    const [isShowPassword, setIsShowPassword] = useState(false);

    const onClickShowPassword = () => setIsShowPassword((show) => !show);

    return (
        <TextField
            margin="normal"
            required
            fullWidth
            size="medium"
            label={label}
            autoComplete="off"
            variant="outlined"
            type={type === 'password' && isShowPassword ? 'text' : type}
            value={value}
            onChange={onChange}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            {label === 'Email' ? <EmailIcon sx={{ fontSize: 15 }} /> : <KeyIcon sx={{ fontSize: 15 }} />}
                        </InputAdornment>
                    ),
                    endAdornment: (
                        type === 'password' && (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={onClickShowPassword}
                                    edge="end"
                                >
                                    {isShowPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    ),
                },
            }}
        />
    );
};

const LoginPage = () => {
    const {
        username,
        setUsername,
        pwd,
        setPwd,
        onSubmit,
        isLoading,
        isAlert,
        setIsAlert,
        alertMessage,
        setAlertMessage,
    } = useLogin();
    const { onSsoLogin, isLoading: isSsoLoading } = useSsoLogin({ setIsAlert, setAlertMessage, shouldProcessRedirect: true });
    const { t } = useTranslation();

    return (
        <>
            <LoginTemplate bgLogin={bgLogin}>

                {isAlert && <Snackbar open={isAlert} onClose={() => setIsAlert(false)} autoHideDuration={AUTO_HIDE_DURATION} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ position: 'absolute', width: '100%' }}>
                    <Alert severity='error'>{alertMessage}</Alert>
                </Snackbar>}
                <Box
                    sx={{
                        my: 4,
                        mx: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            paddingTop: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }}>
                        <Typography component="h1" variant="h6">
                            {t("login.hello")}
                        </Typography>
                        <Typography component="h1" variant="h6" sx={{ mb: 3 }}>
                            {t("login.welcome")}
                        </Typography>
                        {t("login.infoHBscan")}
                    </Box>
                    <Box component="form" onSubmit={onSubmit} noValidate>
                        <Grid container alignItems="center">
                            <Grid>
                                <Typography component="h1" variant="h5">
                                    {t("login.login")}
                                </Typography>
                            </Grid>
                            <Grid>
                                <Avatar alt="hbscanLogo" variant="square" sx={{ background: "none", width: 150, height: 50 }}><LogoSvg /></Avatar>
                            </Grid>
                        </Grid>
                        <CustomTextField
                            label={t("login.email")}
                            type="email"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                        <CustomTextField
                            label={t("login.password")}
                            type="password"
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                        />
                        <Grid container alignItems="center">
                            <Grid>
                                {/* incoming */}
                                {/* <FormControlLabel
                                    control={<Checkbox checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} color="primary" />}
                                    label={t("login.rememberMe")}
                                /> */}
                            </Grid>
                            <Grid>
                                <Link href={routes.emailReset}>
                                    {t("login.forgotPassword")}
                                </Link>
                            </Grid>
                        </Grid>
                        <Box
                            sx={{
                            }}
                        >
                            <Progress isLoading={isLoading || isSsoLoading}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3 }}
                                >
                                    {t("login.login")}
                                </Button>
                                <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={onSsoLogin} startIcon={<MicrosoftIcon />}>{t("login.microsftConnect")}</Button>
                            </Progress>
                        </Box>
                    </Box>
                </Box>
            </LoginTemplate>
        </>
    );
};

export default LoginPage;